import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import { differenceInDays, format } from "date-fns";
import ConfirmDeleteDialog from "features/general/ConfirmDeleteDialog";
import {
  AccessibleUser,
  deleteFileEntry,
  FileEntry,
  useAppDispatch,
  useAppSelector,
} from "model";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { downloadFileHelper } from "./downloadFileHelper";
import { FILE_EDIT_PERIOD_DAYS } from "./FileEntryMoreMenu";
import UpdateFileEntry from "./UpdateFileEntry";

interface CollapsibleFileTableProps {
  bucketedFileEntries: { [associatedUserId: string]: FileEntry[] };
  allAccessibleUsersMap: { [guid: string]: AccessibleUser };
  currentUserId?: string;
}

function Row({
  associatedUserId,
  fileEntries,
  accessibleUser,
  currentUserId,
  allAccessibleUsersMap,
}: {
  associatedUserId: string;
  fileEntries: FileEntry[];
  accessibleUser?: AccessibleUser;
  currentUserId?: string;
  allAccessibleUsersMap: { [guid: string]: AccessibleUser };
}) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user.details);
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileEntry | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const handleDownload = async (
    event: React.MouseEvent<HTMLElement>,
    file: FileEntry
  ) => {
    event.stopPropagation();
    mixpanel.track("Download file clicked");
    await downloadFileHelper(file.guid, dispatch, notify);
  };

  const handleEdit = (
    event: React.MouseEvent<HTMLElement>,
    file: FileEntry
  ) => {
    event.stopPropagation();
    mixpanel.track("Edit/view fileEntry event clicked");
    setSelectedFile(file);
    setEditDialogOpen(true);
  };

  const handleDelete = (
    event: React.MouseEvent<HTMLElement>,
    file: FileEntry
  ) => {
    event.stopPropagation();
    mixpanel.track("Delete fileEntry event clicked");
    setSelectedFile(file);
    setDeleteDialogOpen(true);
  };

  const canEdit = (file: FileEntry) => {
    return (
      differenceInDays(
        Date.now(),
        file.creationDate ? new Date(file.creationDate) : Date.now()
      ) <= FILE_EDIT_PERIOD_DAYS && file.userId === user?.guid
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MMM d, yyyy h:mm a");
    } catch {
      return "-";
    }
  };

  const getFileTypeName = (type: string) => {
    return t(`fileEntries.fileUploadType.label.${type}`);
  };

  const isUploadedByCurrentUser = (file: FileEntry) => {
    return file.userId === currentUserId;
  };

  return (
    <>
      {/* Collapsible header row */}
      <TableRow
        sx={{
          backgroundColor: "#f9fafb",
          borderBottom: "1px solid var(--base/border, #e4e4e7)",
          "&:hover": {
            backgroundColor: "#f3f4f6",
          },
        }}
      >
        <TableCell colSpan={6} sx={{ padding: "12px 24px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
              sx={{ padding: "4px" }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "var(--base/foreground, #18181b)",
                }}
              >
                Uploaded for:{" "}
                {accessibleUser?.name ?? accessibleUser?.mail ?? "Unknown"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "var(--base/muted-foreground, #71717a)",
                  mt: 0.5,
                }}
              >
                {fileEntries.length} file{fileEntries.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
        </TableCell>
      </TableRow>
      {open &&
        fileEntries.map((file) => (
          <TableRow
            key={file.guid}
            sx={{
              borderBottom: "1px solid var(--base/border, #e4e4e7)",
            }}
          >
            {/* Type with icon */}
            <TableCell sx={{ padding: "12px 24px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0px",
                }}
              >
                <Box
                  sx={{
                    width: "40px",
                    paddingTop: "4px",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "4px",
                      backgroundColor: "#f4f4f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <InsertDriveFileIcon
                      sx={{ color: "#71717a", fontSize: "18px" }}
                    />
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: "var(--base/foreground, #18181b)",
                    paddingLeft: "8px",
                    paddingTop: "2px",
                    wordBreak: "break-word",
                  }}
                >
                  {getFileTypeName(file.fileUploadType)}
                </Typography>
              </Box>
            </TableCell>

            {/* Uploaded by */}
            <TableCell sx={{ padding: "12px 16px" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "var(--base/foreground, #18181b)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file.userMail || file.userName || "Unknown"}
              </Typography>
            </TableCell>

            {/* File for (Associated User) */}
            <TableCell sx={{ padding: "12px 16px" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "var(--base/foreground, #18181b)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {allAccessibleUsersMap[file.associatedUserId]?.name ||
                  allAccessibleUsersMap[file.associatedUserId]?.mail ||
                  "Unknown"}
              </Typography>
            </TableCell>

            {/* File name */}
            <TableCell sx={{ padding: "12px 16px" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "var(--base/foreground, #18181b)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file.fileName ||
                  file.fileDescription ||
                  getFileTypeName(file.fileUploadType)}
              </Typography>
            </TableCell>

            {/* Uploaded at */}
            <TableCell sx={{ padding: "12px 16px" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "var(--base/foreground, #18181b)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {formatDate(file.creationDate)}
              </Typography>
            </TableCell>

            {/* Actions */}
            <TableCell sx={{ padding: "12px 16px" }}>
              <Box sx={{ display: "flex", gap: "4px" }}>
                <Tooltip title={t("fileEntry.menu.download")}>
                  <IconButton
                    onClick={(e) => handleDownload(e, file)}
                    size="small"
                    sx={{
                      padding: "6px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canEdit(file) && (
                  <>
                    <Tooltip title={t("fileEntry.menu.editView")}>
                      <IconButton
                        onClick={(e) => handleEdit(e, file)}
                        size="small"
                        sx={{
                          padding: "6px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("fileEntry.menu.delete")}>
                      <IconButton
                        onClick={(e) => handleDelete(e, file)}
                        size="small"
                        sx={{
                          padding: "6px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}

      {/* Edit Dialog */}
      {selectedFile && (
        <UpdateFileEntry
          fileEntry={selectedFile}
          dialogOpen={editDialogOpen}
          setDialogOpen={(open) => {
            setEditDialogOpen(open);
            if (!open) setSelectedFile(null); // Clear selectedFile when dialog closes
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {selectedFile && (
        <ConfirmDeleteDialog
          dialogOpen={deleteDialogOpen}
          deletedObjectDescription={selectedFile.fileName ?? ""}
          closeDialog={() => {
            setDeleteDialogOpen(false);
            setSelectedFile(null); // Clear selectedFile when dialog closes
          }}
          onConfirmDelete={async () => {
            await dispatch(deleteFileEntry(selectedFile));
            setDeleteDialogOpen(false);
            setSelectedFile(null); // Clear selectedFile after delete
          }}
        />
      )}
    </>
  );
}

/**
 * Collapsible file table component for files grouped by associated user
 */
export default function CollapsibleFileTable({
  bucketedFileEntries,
  allAccessibleUsersMap,
  currentUserId,
}: CollapsibleFileTableProps) {
  const totalFiles = Object.values(bucketedFileEntries).reduce(
    (sum, files) => sum + files.length,
    0
  );

  return (
    <Paper
      sx={{
        border: "1px solid var(--base/border, #e4e4e7)",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Table */}
      <TableContainer sx={{ padding: "24px", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            <col style={{ width: "200px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "auto" }} />
            <col style={{ width: "200px" }} />
            <col style={{ width: "120px" }} />
          </colgroup>
          <TableHead>
            <TableRow
              sx={{
                borderBottom: "1px solid var(--base/border, #e4e4e7)",
              }}
            >
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--base/muted-foreground, #71717a)",
                  padding: "12px 24px",
                }}
              >
                Type
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--base/muted-foreground, #71717a)",
                  padding: "12px 16px",
                }}
              >
                Uploaded by
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--base/muted-foreground, #71717a)",
                  padding: "12px 16px",
                }}
              >
                File for
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--base/muted-foreground, #71717a)",
                  padding: "12px 16px",
                }}
              >
                File name
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--base/muted-foreground, #71717a)",
                  padding: "12px 16px",
                }}
              >
                Uploaded at
              </TableCell>
              <TableCell
                sx={{
                  padding: "12px 16px",
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {totalFiles === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ padding: "48px 16px" }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "var(--base/muted-foreground, #71717a)",
                    }}
                  >
                    No files found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(bucketedFileEntries).map(
                ([associatedUserId, fileEntries]) => (
                  <Row
                    key={associatedUserId}
                    associatedUserId={associatedUserId}
                    fileEntries={fileEntries}
                    accessibleUser={allAccessibleUsersMap[associatedUserId]}
                    currentUserId={currentUserId}
                    allAccessibleUsersMap={allAccessibleUsersMap}
                  />
                )
              )
            )}
          </TableBody>
        </Table>

        {/* Table Footer / Caption */}
        {totalFiles > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "16px 0 0 0",
              minHeight: "36px",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                lineHeight: "16px",
                color: "var(--base/muted-foreground, #71717a)",
              }}
            >
              Showing <strong>1-{totalFiles}</strong> of{" "}
              <strong>{totalFiles}</strong> files
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Paper>
  );
}
